import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';

// Product Attributes Modal V2 - Simplified with tabs to manage attribute types
function ProductAttributesModalV2({ products, onSaveBulk, isOpen, onClose, initialTab = 'assign' }) {
  const [activeTab, setActiveTab] = useState(initialTab);
  
  // Product selections
  const [productSelections, setProductSelections] = useState({});
  
  // Attribute type categories (loaded from API, not hard-coded)
  const [attributeTypes, setAttributeTypes] = useState([]);
  
  // All attributes stored by type ID - loaded from API
  const [attributesByType, setAttributesByType] = useState({});
  
  // Track which attribute types are expanded
  const [expandedTypes, setExpandedTypes] = useState(['paper_types', 'border_options']);
  
  const [saving, setSaving] = useState(false);
  const [draggedAttr, setDraggedAttr] = useState(null);
  const [draggedType, setDraggedType] = useState(null);

  // Set initial tab when modal opens
  useEffect(() => {
    if (isOpen) {
      setActiveTab(initialTab);
    }
  }, [isOpen, initialTab]);

  // Load attributes from API when modal opens
  useEffect(() => {
    if (isOpen) {
      loadAttributesFromAPI();
    }
  }, [isOpen]);

  // Initialize product selections when attributes are loaded
  useEffect(() => {
    if (products && isOpen && attributeTypes.length > 0) {
      const selections = {};
      products.forEach(([sku, product]) => {
        const productSelection = {};
        
        // Map paper_types and border_options from product data
        productSelection['paper_types'] = (product.paper_types || []).map(p => typeof p === 'string' ? p : p.id);
        productSelection['border_options'] = (product.border_options || []).map(b => typeof b === 'string' ? b : b.id);
        
        // Initialize empty arrays for any other attribute types
        attributeTypes.forEach(type => {
          if (!productSelection[type.id]) {
            productSelection[type.id] = [];
          }
        });
        
        selections[sku] = productSelection;
      });
      setProductSelections(selections);
    }
  }, [products, isOpen, attributeTypes]);

  const loadAttributesFromAPI = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/attributes`);
      const data = response.data;
      
      // Extract attribute types
      const types = data.map(item => item.type);
      setAttributeTypes(types);
      
      // Build attributesByType object
      const byType = {};
      data.forEach(item => {
        byType[item.type.id] = item.attributes;
      });
      setAttributesByType(byType);
      
      // Also expand all types by default
      setExpandedTypes(types.map(t => t.id));
    } catch (error) {
      console.error('Failed to load attributes:', error);
      toast.error('Failed to load attributes from server');
    }
  };

  const toggleAttribute = (sku, typeId, attrId) => {
    setProductSelections(prev => {
      const current = prev[sku] || {};
      const currentTypeAttrs = current[typeId] || [];
      const updated = currentTypeAttrs.includes(attrId)
        ? currentTypeAttrs.filter(id => id !== attrId)
        : [...currentTypeAttrs, attrId];
      
      return { ...prev, [sku]: { ...current, [typeId]: updated } };
    });
  };

  const handleBulkSave = async () => {
    setSaving(true);
    try {
      const paperTypes = attributesByType['paper_types'] || [];
      const borderOptions = attributesByType['border_options'] || [];
      
      const updates = Object.entries(productSelections).map(([sku, selections]) => ({
        sku,
        // Keep paper_types and border_options for backward compatibility with backend
        paper_types: paperTypes.filter(p => (selections['paper_types'] || []).includes(p.id)),
        border_options: borderOptions.filter(b => (selections['border_options'] || []).includes(b.id))
        // TODO: In the future, save all custom attribute types to backend as well
      }));
      
      await onSaveBulk(updates);
      toast.success('✅ Attributes saved for all products!');
    } catch (error) {
      console.error('Failed to save attributes:', error);
      toast.error('Failed to save attributes');
    } finally {
      setSaving(false);
      setTimeout(() => onClose(), 100);
    }
  };

  const handleSaveAttributes = async () => {
    try {
      // Flatten attributesByType into a single array of attributes
      const allAttributes = [];
      Object.entries(attributesByType).forEach(([typeId, attrs]) => {
        attrs.forEach(attr => {
          allAttributes.push({
            ...attr,
            type_id: typeId  // Ensure type_id is set
          });
        });
      });
      
      // Send to backend
      await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/admin/attributes`, {
        attribute_types: attributeTypes,
        attributes: allAttributes
      });
      
      toast.success('✅ All attributes saved to database!');
      setTimeout(() => onClose(), 100);
    } catch (error) {
      console.error('Failed to save attributes:', error);
      toast.error('Failed to save attributes to server');
    }
  };

  // Attribute management functions
  const handleAttributeChange = (type, id, field, value) => {
    if (type === 'paper') {
      setGlobalPaperTypes(prev => prev.map(attr => 
        attr.id === id ? { ...attr, [field]: value } : attr
      ));
    } else {
      setGlobalBorderOptions(prev => prev.map(attr => 
        attr.id === id ? { ...attr, [field]: value } : attr
      ));
    }
  };

  const handleAddAttribute = (type) => {
    const newId = prompt(`Enter new ${type === 'paper' ? 'Paper Type' : 'Border Option'} ID (e.g., "L" for Luster):`);
    if (!newId) return;
    
    const newName = prompt('Enter name:');
    if (!newName) return;

    const newAttr = {
      id: newId.toUpperCase(),
      name: newName,
      description: '',
      order_index: type === 'paper' ? globalPaperTypes.length : globalBorderOptions.length
    };

    if (type === 'paper') {
      setGlobalPaperTypes(prev => [...prev, newAttr]);
    } else {
      setGlobalBorderOptions(prev => [...prev, newAttr]);
    }
    toast.success(`✅ ${newName} added!`);
  };

  const handleDeleteAttribute = (type, id) => {
    if (!window.confirm('Delete this attribute? This will remove it from all products.')) return;
    
    if (type === 'paper') {
      setGlobalPaperTypes(prev => prev.filter(attr => attr.id !== id));
    } else {
      setGlobalBorderOptions(prev => prev.filter(attr => attr.id !== id));
    }
    toast.success('✅ Attribute deleted!');
  };

  // Drag and drop
  const handleDragStart = (type, index) => {
    setDraggedAttr({ type, index });
  };

  const handleDragOver = (e, type, index) => {
    e.preventDefault();
    if (!draggedAttr || draggedAttr.type !== type || draggedAttr.index === index) return;

    const list = type === 'paper' ? globalPaperTypes : globalBorderOptions;
    const setList = type === 'paper' ? setGlobalPaperTypes : setGlobalBorderOptions;
    
    const newOrder = [...list];
    const draggedItem = newOrder[draggedAttr.index];
    newOrder.splice(draggedAttr.index, 1);
    newOrder.splice(index, 0, draggedItem);
    
    newOrder.forEach((item, idx) => { item.order_index = idx; });
    
    setList(newOrder);
    setDraggedAttr({ type, index });
  };

  const handleDragEnd = () => {
    setDraggedAttr(null);
  };

  // Attribute type management functions
  const handleAttributeTypeChange = (id, field, value) => {
    setAttributeTypes(prev => prev.map(type => 
      type.id === id ? { ...type, [field]: value } : type
    ));
  };

  const handleAddAttributeType = () => {
    const newId = prompt('Enter attribute type ID (e.g., "finish_types"):');
    if (!newId) return;
    
    const newName = prompt('Enter attribute type name (e.g., "Finish Types"):');
    if (!newName) return;

    const newType = {
      id: newId.toLowerCase().replace(/\s+/g, '_'),
      name: newName,
      description: '',
      order_index: attributeTypes.length
    };

    setAttributeTypes(prev => [...prev, newType]);
    toast.success(`✅ ${newName} added!`);
  };

  const handleDeleteAttributeType = (id) => {
    if (!window.confirm('Delete this attribute type? This action cannot be undone.')) return;
    
    setAttributeTypes(prev => prev.filter(type => type.id !== id));
    toast.success('✅ Attribute type deleted!');
  };

  // Drag and drop for attribute types
  const handleTypeDragStart = (index) => {
    setDraggedType(index);
  };

  const handleTypeDragOver = (e, index) => {
    e.preventDefault();
    if (draggedType === null || draggedType === index) return;

    const newOrder = [...attributeTypes];
    const draggedItem = newOrder[draggedType];
    newOrder.splice(draggedType, 1);
    newOrder.splice(index, 0, draggedItem);
    
    newOrder.forEach((item, idx) => { item.order_index = idx; });
    
    setAttributeTypes(newOrder);
    setDraggedType(index);
  };

  const handleTypeDragEnd = () => {
    setDraggedType(null);
  };

  // Toggle expanded state for attribute type
  const toggleTypeExpanded = (typeId) => {
    setExpandedTypes(prev => 
      prev.includes(typeId) 
        ? prev.filter(id => id !== typeId)
        : [...prev, typeId]
    );
  };

  // Get attributes for a specific type
  const getAttributesForType = (typeId) => {
    return attributesByType[typeId] || [];
  };

  // Set attributes for a specific type
  const setAttributesForType = (typeId, attributes) => {
    setAttributesByType(prev => ({
      ...prev,
      [typeId]: attributes
    }));
  };

  // Add attribute to a specific type
  const handleAddAttributeToType = (typeId) => {
    const typeName = attributeTypes.find(t => t.id === typeId)?.name || 'Attribute';
    const newName = prompt(`Enter new ${typeName} name:`);
    if (!newName) return;

    const newId = prompt(`Enter ID for ${newName} (e.g., ${newName.substring(0, 3).toUpperCase()}):`);
    if (!newId) return;

    const attributes = getAttributesForType(typeId);
    const newAttr = {
      id: newId.toUpperCase(),
      name: newName,
      description: '',
      order_index: attributes.length
    };

    setAttributesForType(typeId, [...attributes, newAttr]);
    toast.success(`✅ ${newName} added to ${typeName}!`);
  };

  // Delete attribute from a specific type
  const handleDeleteAttributeFromType = (typeId, attrId) => {
    const typeName = attributeTypes.find(t => t.id === typeId)?.name || 'Attribute';
    if (!window.confirm(`Delete this ${typeName}? This action cannot be undone.`)) return;
    
    const attributes = getAttributesForType(typeId);
    setAttributesForType(typeId, attributes.filter(attr => attr.id !== attrId));
    toast.success('✅ Attribute deleted!');
  };

  if (!isOpen) return null;

  const productArray = products ? products.map(([sku, product]) => ({ sku, ...product })) : [];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-[90vw] max-w-[90vw] max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Product Attributes</DialogTitle>
          <DialogDescription>
            Assign attributes to products or manage attribute types
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="assign">Assign to Products</TabsTrigger>
            <TabsTrigger value="manage">Manage Attributes</TabsTrigger>
          </TabsList>

          <TabsContent value="manage" className="flex-1 overflow-y-auto max-h-96">
            <div className="space-y-4">
              <div className="flex justify-between items-center mb-4">
                <p className="text-sm text-gray-600">
                  Manage attribute types and their values. Click to expand/collapse each type.
                </p>
                <Button onClick={handleAddAttributeType} className="bg-green-600 hover:bg-green-700">
                  + Add Attribute Type
                </Button>
              </div>
              
              {attributeTypes.sort((a, b) => a.order_index - b.order_index).map((type, index) => {
                const isExpanded = expandedTypes.includes(type.id);
                const attributes = getAttributesForType(type.id);
                
                return (
                  <div
                    key={type.id}
                    className="border rounded-lg overflow-hidden"
                  >
                    {/* Attribute Type Header */}
                    <div
                      draggable
                      onDragStart={() => handleTypeDragStart(index)}
                      onDragOver={(e) => handleTypeDragOver(e, index)}
                      onDragEnd={handleTypeDragEnd}
                      className={`p-4 bg-gray-50 border-b transition-all ${
                        draggedType === index ? 'opacity-50 bg-blue-100' : 'hover:bg-gray-100'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <svg className="w-5 h-5 text-gray-400 cursor-move" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
                        </svg>
                        
                        <button
                          onClick={() => toggleTypeExpanded(type.id)}
                          className="p-1 hover:bg-gray-200 rounded"
                        >
                          <svg className={`w-5 h-5 transition-transform ${isExpanded ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </button>

                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <Badge variant="secondary" className="text-xs font-mono">{type.id}</Badge>
                            <input
                              type="text"
                              value={type.name}
                              onChange={(e) => handleAttributeTypeChange(type.id, 'name', e.target.value)}
                              className="px-3 py-1 border rounded font-semibold text-gray-900"
                              placeholder="Type Name"
                              onClick={(e) => e.stopPropagation()}
                            />
                            <span className="text-sm text-gray-500">({attributes.length} attributes)</span>
                          </div>
                          <input
                            type="text"
                            value={type.description || ''}
                            onChange={(e) => handleAttributeTypeChange(type.id, 'description', e.target.value)}
                            placeholder="Description (optional)"
                            className="w-full px-3 py-1 text-sm border rounded text-gray-600"
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>

                        <Button
                          size="sm"
                          variant="outline"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteAttributeType(type.id);
                          }}
                          className="text-red-600 hover:bg-red-50"
                        >
                          Delete Type
                        </Button>
                      </div>
                    </div>

                    {/* Nested Attributes */}
                    {isExpanded && (
                      <div className="p-4 bg-white space-y-2">
                        {attributes.length === 0 ? (
                          <div className="text-center py-8 text-gray-500">
                            <p className="mb-3">No attributes yet for {type.name}</p>
                            <Button
                              size="sm"
                              onClick={() => handleAddAttributeToType(type.id)}
                              className="bg-blue-600 hover:bg-blue-700"
                            >
                              + Add First Attribute
                            </Button>
                          </div>
                        ) : (
                          <>
                            {attributes.sort((a, b) => a.order_index - b.order_index).map((attr, attrIndex) => (
                              <div
                                key={attr.id}
                                draggable
                                onDragStart={() => setDraggedAttr({ typeId: type.id, index: attrIndex })}
                                onDragOver={(e) => {
                                  e.preventDefault();
                                  if (!draggedAttr || draggedAttr.typeId !== type.id || draggedAttr.index === attrIndex) return;
                                  
                                  const newAttrs = [...attributes];
                                  const draggedItem = newAttrs[draggedAttr.index];
                                  newAttrs.splice(draggedAttr.index, 1);
                                  newAttrs.splice(attrIndex, 0, draggedItem);
                                  
                                  newAttrs.forEach((item, idx) => { item.order_index = idx; });
                                  setAttributesForType(type.id, newAttrs);
                                  setDraggedAttr({ typeId: type.id, index: attrIndex });
                                }}
                                onDragEnd={() => setDraggedAttr(null)}
                                className={`flex items-center gap-3 p-3 border rounded ml-8 transition-all cursor-move ${
                                  draggedAttr?.typeId === type.id && draggedAttr?.index === attrIndex 
                                    ? 'opacity-50 bg-blue-50' 
                                    : 'hover:bg-gray-50'
                                }`}
                              >
                                <svg className="w-4 h-4 text-gray-400 cursor-move" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
                                </svg>
                                
                                <Badge variant="outline" className="text-xs font-mono">{attr.id}</Badge>
                                
                                <input
                                  type="text"
                                  value={attr.name}
                                  onChange={(e) => {
                                    const updated = attributes.map(a => 
                                      a.id === attr.id ? { ...a, name: e.target.value } : a
                                    );
                                    setAttributesForType(type.id, updated);
                                  }}
                                  className="flex-1 px-3 py-1 border rounded"
                                  placeholder="Attribute name"
                                />
                                
                                <input
                                  type="text"
                                  value={attr.description || ''}
                                  onChange={(e) => {
                                    const updated = attributes.map(a => 
                                      a.id === attr.id ? { ...a, description: e.target.value } : a
                                    );
                                    setAttributesForType(type.id, updated);
                                  }}
                                  className="flex-1 px-3 py-1 border rounded text-sm text-gray-600"
                                  placeholder="Description (optional)"
                                />

                                {/* Price Surcharge Field with Type Selection */}
                                <div className="flex items-center gap-2">
                                  <select
                                    value={attr.price_type || 'flat'}
                                    onChange={(e) => {
                                      const updated = attributes.map(a => 
                                        a.id === attr.id ? { ...a, price_type: e.target.value } : a
                                      );
                                      setAttributesForType(type.id, updated);
                                    }}
                                    className="px-2 py-1 border rounded text-sm text-gray-600 w-20"
                                  >
                                    <option value="flat">$</option>
                                    <option value="percentage">%</option>
                                  </select>
                                  
                                  {attr.price_type === 'percentage' ? (
                                    <div className="flex items-center gap-1">
                                      <input
                                        type="number"
                                        step="0.1"
                                        value={attr.percentage || ''}
                                        onChange={(e) => {
                                          const percentage = parseFloat(e.target.value) || 0;
                                          const updated = attributes.map(a => 
                                            a.id === attr.id ? { ...a, percentage: percentage } : a
                                          );
                                          setAttributesForType(type.id, updated);
                                        }}
                                        className="w-20 px-2 py-1 border rounded text-sm text-gray-600"
                                        placeholder="15.5"
                                        title="Percentage change (e.g., 15.5 for 15.5% increase, -10 for 10% discount)"
                                      />
                                      <span className="text-sm text-gray-600">%</span>
                                      <span className="text-xs text-blue-600 min-w-[60px]">
                                        {(attr.percentage || attr.percentage === 0) ? (
                                          attr.percentage >= 0 ? `+${attr.percentage}%` : `${attr.percentage}%`
                                        ) : ''}
                                      </span>
                                    </div>
                                  ) : (
                                    <div className="flex items-center gap-1">
                                      <span className="text-sm text-gray-600">$</span>
                                      <input
                                        type="number"
                                        step="0.01"
                                        value={((attr.price || 0) / 100) || ''}
                                        onChange={(e) => {
                                          const dollars = parseFloat(e.target.value) || 0;
                                          const cents = Math.round(dollars * 100);
                                          const updated = attributes.map(a => 
                                            a.id === attr.id ? { ...a, price: cents } : a
                                          );
                                          setAttributesForType(type.id, updated);
                                        }}
                                        className="w-20 px-2 py-1 border rounded text-sm text-gray-600"
                                        placeholder="0.50"
                                        title="Additional flat fee in AUD (e.g., 0.50 for $0.50 surcharge, -0.25 for $0.25 discount)"
                                      />
                                      <span className="text-xs text-blue-600 min-w-[60px]">
                                        {(attr.price || attr.price === 0) ? (
                                          attr.price >= 0 ? `+$${(attr.price / 100).toFixed(2)}` : `-$${Math.abs(attr.price / 100).toFixed(2)}`
                                        ) : ''}
                                      </span>
                                    </div>
                                  )}
                                </div>

                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleDeleteAttributeFromType(type.id, attr.id)}
                                  className="text-red-600 hover:bg-red-50"
                                >
                                  Delete
                                </Button>
                              </div>
                            ))}
                            
                            <div className="ml-8 pt-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleAddAttributeToType(type.id)}
                                className="text-blue-600 border-blue-300 hover:bg-blue-50"
                              >
                                + Add {type.name} Attribute
                              </Button>
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </TabsContent>

          <TabsContent value="assign" className="flex-1 overflow-y-auto max-h-96">
            <div className="space-y-4">
              {productArray.map((product) => (
                <Card key={product.sku} className="p-4">
                  <div className="font-medium mb-3">{product.name || product.size} ({product.sku})</div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    {attributeTypes.sort((a, b) => a.order_index - b.order_index).map((type) => {
                      const attributes = attributesByType[type.id] || [];
                      if (attributes.length === 0) return null; // Don't show empty types
                      
                      return (
                        <div key={type.id}>
                          <div className="text-sm font-medium mb-2">{type.name}:</div>
                          <div className="space-y-1">
                            {attributes.map(attr => (
                              <label key={attr.id} className="flex items-center gap-2 text-sm">
                                <input
                                  type="checkbox"
                                  checked={(productSelections[product.sku]?.[type.id] || []).includes(attr.id)}
                                  onChange={() => toggleAttribute(product.sku, type.id, attr.id)}
                                  className="rounded"
                                />
                                <span>{attr.name}</span>
                                <Badge variant="secondary" className="text-xs">{attr.id}</Badge>
                              </label>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </Card>
              ))}
            </div>
          </TabsContent>
        </Tabs>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          {activeTab === 'assign' ? (
            <Button onClick={handleBulkSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700">
              {saving ? 'Saving...' : 'Save All Products'}
            </Button>
          ) : (
            <Button onClick={handleSaveAttributes} className="bg-green-600 hover:bg-green-700">
              💾 Save Attributes
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ProductAttributesModalV2;
